import * as fs from 'fs';
import * as path from 'path';

const SOURCE_DIRS = ['src', 'components', 'app', 'lib'] as const;

const INTRO_PAGE_CANDIDATES = [
  'src/components/IntroPage.tsx',
  'components/IntroPage.tsx',
  'app/components/IntroPage.tsx',
] as const;

const SURVEY_PAGE_CANDIDATES = [
  'src/components/SurveyPage.tsx',
  'components/SurveyPage.tsx',
  'app/components/SurveyPage.tsx',
] as const;

const END_PAGE_CANDIDATES = [
  'src/components/EndPage.tsx',
  'components/EndPage.tsx',
  'app/components/EndPage.tsx',
] as const;

const QUESTION_CANDIDATES = [
  'src/components/Question.tsx',
  'components/Question.tsx',
  'app/components/Question.tsx',
] as const;

const MATRIX_SCALE_FILES = [
  'CsatMatrixScale.tsx',
  'SliderMatrixScale.tsx',
  'LikertMatrixScale.tsx',
] as const;

const MATRIX_RELATED_FILES = [
  ...MATRIX_SCALE_FILES,
  'MatrixDropdown.tsx',
] as const;

const AGENT_AUTHORED_STYLE_FILES = [
  'src/components/SurveyPage.tsx',
  'components/SurveyPage.tsx',
  'app/components/SurveyPage.tsx',
  'src/globals.css',
  'src/index.css',
  'src/app/globals.css',
  'app/globals.css',
  'src/app/layout.tsx',
  'app/layout.tsx',
] as const;

const HARDCODED_HEX = /#[0-9A-Fa-f]{3,8}\b/;
const HARDCODED_RGB = /\brgba?\s*\(/;

function collectSourceFiles(projectRoot: string): string[] {
  const files: string[] = [];
  for (const dir of SOURCE_DIRS) {
    const abs = path.join(projectRoot, dir);
    if (!fs.existsSync(abs)) continue;
    walk(abs, files);
  }
  return files;
}

function walk(dir: string, out: string[]): void {
  for (const entry of fs.readdirSync(dir, { withFileTypes: true })) {
    const abs = path.join(dir, entry.name);
    if (entry.isDirectory()) {
      if (entry.name === 'node_modules' || entry.name === '.next' || entry.name === 'dist') {
        continue;
      }
      walk(abs, out);
    } else if (/\.(tsx?|jsx?|mjs|cjs)$/.test(entry.name)) {
      out.push(abs);
    }
  }
}

function findFirstExisting(projectRoot: string, candidates: readonly string[]): string | null {
  for (const rel of candidates) {
    const abs = path.join(projectRoot, rel);
    if (fs.existsSync(abs)) return rel;
  }
  return null;
}

function isAllowedStyleLine(line: string): boolean {
  const trimmed = line.trim();
  if (trimmed.startsWith('//') || trimmed.startsWith('*') || trimmed.startsWith('/*')) return true;
  if (trimmed.includes('var(--cfm-')) return true;
  if (/url\s*\(#/.test(trimmed)) return true;
  return false;
}

export function scanHardcodedStyleIssues(projectRoot: string): string[] {
  const issues: string[] = [];

  for (const rel of AGENT_AUTHORED_STYLE_FILES) {
    const abs = path.join(projectRoot, rel);
    if (!fs.existsSync(abs)) continue;

    const content = fs.readFileSync(abs, 'utf8');
    const lines = content.split('\n');

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (isAllowedStyleLine(line)) continue;
      if (HARDCODED_HEX.test(line) || HARDCODED_RGB.test(line)) {
        issues.push(`${rel}:${i + 1}: hardcoded color — use survey-ui-config.json + var(--cfm-*)`);
      }
    }
  }

  return issues;
}

export function scanIntroStartIssues(projectRoot: string): string[] {
  const issues: string[] = [];
  const introRel = findFirstExisting(projectRoot, INTRO_PAGE_CANDIDATES);

  if (!introRel) {
    issues.push(
      'IntroPage.tsx missing — author an intro page with a Start button that dispatches onAction START',
    );
    return issues;
  }

  const introContent = fs.readFileSync(path.join(projectRoot, introRel), 'utf8');

  if (!/data-cfm-btn-start/.test(introContent)) {
    issues.push(`${introRel}: Start button missing data-cfm-btn-start`);
  }
  if (!/onClick=\{onStart\}|onClick=\{\s*onStart\s*\}/.test(introContent)) {
    issues.push(`${introRel}: Start button must use onClick={onStart}`);
  }
  if (/\{showStart &&|showStart &&/.test(introContent)) {
    issues.push(`${introRel}: conditional Start (showStart) — always render Start button`);
  }

  const surveyRel = findFirstExisting(projectRoot, SURVEY_PAGE_CANDIDATES);
  if (!surveyRel) return issues;

  const surveyContent = fs.readFileSync(path.join(projectRoot, surveyRel), 'utf8');
  const hasIntroPhase = /phase\.kind\s*===\s*['"]intro['"]|kind\s*===\s*['"]intro['"]/.test(
    surveyContent,
  );

  if (hasIntroPhase && !/<IntroPage/.test(surveyContent)) {
    issues.push(
      `${surveyRel}: intro phase must render <IntroPage /> — do not inline header/description only`,
    );
  }
  if (/<IntroPage/.test(surveyContent) && !/type:\s*['"]START['"]/.test(surveyContent)) {
    issues.push(`${surveyRel}: IntroPage must wire onAction({ type: 'START' })`);
  }

  return issues;
}

function endPageFiltersEmailCtAs(content: string): boolean {
  if (/endPageCtas\s*\(/.test(content)) return true;
  if (/sendEmailResponse,\s*resumptionNotification,\s*reopenNotification/.test(content)) {
    return true;
  }
  return false;
}

export function scanEndPageCtaIssues(projectRoot: string): string[] {
  const endRel = findFirstExisting(projectRoot, END_PAGE_CANDIDATES);
  if (!endRel) return [];

  const content = fs.readFileSync(path.join(projectRoot, endRel), 'utf8');
  if (!/<SpecialPageCtaSection/.test(content)) return [];

  if (endPageFiltersEmailCtAs(content)) return [];

  if (/ctas=\{[^}]*page\.ctas[^}]*\}/.test(content)) {
    return [
      `${endRel}: EndPage must filter email CTAs via endPageCtas(page.ctas) before SpecialPageCtaSection — see 15-end-page.md`,
    ];
  }

  return [];
}

export interface MatrixVariantPlanRow {
  questionId: string;
  type: string;
  displayStyle: string;
  statementLayout: string;
  transposeTable: string;
  selectionMode: string;
}

export function readMatrixVariantsFromPlan(projectRoot: string): MatrixVariantPlanRow[] {
  const planPath = path.join(projectRoot, 'implementation_plan.md');
  if (!fs.existsSync(planPath)) return [];

  const content = fs.readFileSync(planPath, 'utf8');
  const section = content.split('## 3c. Matrix variant inventory')[1];
  if (!section) return [];

  const tablePart = section.split('##')[0] ?? '';
  const lines = tablePart.split('\n').filter((l) => l.trim().startsWith('|'));
  const rows: MatrixVariantPlanRow[] = [];

  for (const line of lines) {
    if (line.includes('questionId')) continue;
    // Skip markdown separator rows (only pipes, dashes, colons, whitespace) — including
    // multi-column separators like `|------|------|` that a single-dash regex misses.
    if (/^[\s|:-]+$/.test(line)) continue;
    if (line.includes('(no matrix questions in fetch)')) continue;

    const cells = line
      .split('|')
      .map((c) => c.trim())
      .filter(Boolean);
    if (cells.length < 6) continue;

    rows.push({
      questionId: cells[0],
      type: cells[1],
      displayStyle: cells[2],
      statementLayout: cells[3],
      transposeTable: cells[4],
      selectionMode: cells[5],
    });
  }

  return rows;
}

function readMatrixFileContents(projectRoot: string): Map<string, string> {
  const out = new Map<string, string>();
  for (const file of collectSourceFiles(projectRoot)) {
    const basename = path.basename(file);
    if (!(MATRIX_RELATED_FILES as readonly string[]).includes(basename)) continue;
    out.set(basename, fs.readFileSync(file, 'utf8'));
  }
  return out;
}

function questionDispatcherContent(projectRoot: string): string {
  const rel = findFirstExisting(projectRoot, QUESTION_CANDIDATES);
  if (!rel) return '';
  return fs.readFileSync(path.join(projectRoot, rel), 'utf8');
}

export function scanMatrixLayoutIssues(projectRoot: string): string[] {
  const issues: string[] = [];
  const files = readMatrixFileContents(projectRoot);
  if (files.size === 0) return issues;

  const matrixBlob = [...files.entries()]
    .map(([name, content]) => `// ${name}\n${content}`)
    .join('\n');

  for (const [name, content] of files) {
    if ((MATRIX_SCALE_FILES as readonly string[]).includes(name)) {
      if (!/<select[\s>]/i.test(content) && !/<details[\s>]/i.test(content)) continue;
      issues.push(
        `${name}: matrix dropdown must use custom MatrixDropdown — no native <select> or <details>`,
      );
    }
  }

  const variants = readMatrixVariantsFromPlan(projectRoot);
  const hasBipolar = variants.some(
    (r) => r.type === 'CFM_MATRIX' && /bipolar/i.test(r.statementLayout),
  );
  const hasTranspose = variants.some((r) => /true/i.test(r.transposeTable));
  const hasRatingGraphics = variants.some(
    (r) => r.type === 'RATING_MATRIX' && /graphics/i.test(r.displayStyle),
  );
  const hasDropdownStyle = variants.some((r) => /dropdown|selectbox/i.test(r.displayStyle));

  if (hasBipolar) {
    const likert = files.get('LikertMatrixScale.tsx');
    if (likert && !/oppositeStatementText/.test(likert)) {
      issues.push(
        'LikertMatrixScale.tsx: §3c has bipolar CFM_MATRIX — render oppositeStatementText in right column (see layout-cookbook.md)',
      );
    }
  }

  if (hasTranspose) {
    if (!/transposeTable/.test(matrixBlob)) {
      issues.push(
        'Matrix scale components: §3c has transposeTable — branch with swapped row/col iteration (see layout-cookbook.md)',
      );
    }
  }

  if (hasRatingGraphics) {
    const csat = files.get('CsatMatrixScale.tsx') ?? '';
    if (!/CustomSliderTrack/.test(csat)) {
      issues.push(
        'CsatMatrixScale.tsx: §3c has RATING_MATRIX graphics — use CustomSliderTrack per row, not cell button grid',
      );
    }
    const question = questionDispatcherContent(projectRoot);
    if (question && /RATING_MATRIX[\s\S]{0,400}SliderMatrixScale/.test(question)) {
      issues.push(
        'Question.tsx: RATING_MATRIX graphics must use CsatMatrixScale + CustomSliderTrack — not SliderMatrixScale',
      );
    }
  }

  if (hasDropdownStyle) {
    if (!/MatrixDropdown/.test(matrixBlob)) {
      issues.push(
        'Matrix components: §3c has dropdown/selectbox displayStyle — use MatrixDropdown per row',
      );
    }
  }

  return issues;
}

/**
 * Fail-closed guard for the §3c enforcement chain.
 *
 * The structural matrix checks (bipolar / transpose / graphics / dropdown) are all derived
 * from `implementation_plan.md` §3c. If matrix component files exist but §3c is empty, those
 * checks silently no-op. At the Phase-6 gate (full `verify-build`, run AFTER `sync-from-fetch`)
 * an empty §3c means the agent skipped `sync-from-fetch` or never populated the inventory — so
 * the layout checks never ran. Surface that as a blocking issue instead of passing silently.
 *
 * Only meaningful post-sync: do NOT call this from `--fast` (which legitimately runs before
 * `sync-from-fetch`).
 */
export function scanMatrixInventoryGap(projectRoot: string): string[] {
  const files = readMatrixFileContents(projectRoot);
  if (files.size === 0) return [];

  const variants = readMatrixVariantsFromPlan(projectRoot);
  if (variants.length > 0) return [];

  return [
    'Matrix component files exist but implementation_plan.md §3c is empty — run `npx cfm-sdk sync-from-fetch` and fill §3c, then re-verify (matrix layout checks depend on §3c). See layout-cookbook.md.',
  ];
}

export function scanMatrixStatementIssues(projectRoot: string): string[] {
  const issues: string[] = [];

  for (const file of collectSourceFiles(projectRoot)) {
    const basename = path.basename(file);
    if (!(MATRIX_SCALE_FILES as readonly string[]).includes(basename)) continue;

    const content = fs.readFileSync(file, 'utf8');
    const rel = path.relative(projectRoot, file);
    if (!/shouldShowMatrixStatementLabels/.test(content)) {
      issues.push(
        `${rel}: must call shouldShowMatrixStatementLabels(question) for single- vs multi-row statement labels`,
      );
    }
  }

  return issues;
}

export function readMcqOtherFlagFromPlan(projectRoot: string): boolean | null {
  const planPath = path.join(projectRoot, 'implementation_plan.md');
  if (!fs.existsSync(planPath)) return null;

  const content = fs.readFileSync(planPath, 'utf8');
  const match = content.match(/\*\*MCQ Other option in fetch:\*\*\s*(yes|no)/i);
  if (!match) return null;
  return match[1].toLowerCase() === 'yes';
}

export function scanMcqOtherIssues(projectRoot: string): string[] {
  if (readMcqOtherFlagFromPlan(projectRoot) !== true) return [];

  const questionRel = findFirstExisting(projectRoot, QUESTION_CANDIDATES);
  if (!questionRel) {
    return [
      'Question.tsx missing — survey has MCQ Other options; author MCQ handler with MCQ_OTHER_TEXT_CHANGE',
    ];
  }

  const content = fs.readFileSync(path.join(projectRoot, questionRel), 'utf8');
  if (/MCQ_OTHER_TEXT_CHANGE|buildMcqOptionLevelKey/.test(content)) return [];

  return [
    `${questionRel}: survey has MCQ Other options — wire MCQ_OTHER_TEXT_CHANGE + buildMcqOptionLevelKey — see 02-radio.md`,
  ];
}

export interface SourceScanResult {
  stubMatches: string[];
  internalSdkImports: string[];
  nextImports: string[];
  introStartIssues: string[];
  hardcodedStyleIssues: string[];
  endPageCtaIssues: string[];
  matrixStatementIssues: string[];
  matrixLayoutIssues: string[];
  mcqOtherIssues: string[];
}

export function scanProjectSource(projectRoot: string): SourceScanResult {
  const stubMatches: string[] = [];
  const internalSdkImports: string[] = [];
  const nextImports: string[] = [];

  for (const file of collectSourceFiles(projectRoot)) {
    const content = fs.readFileSync(file, 'utf8');
    const rel = path.relative(projectRoot, file);

    if (content.includes('not yet implemented')) {
      stubMatches.push(`${rel}: stub placeholder text`);
    }
    if (/@explorer02\/cfm-survey-sdk\/src|@repo\/sdk\/src/.test(content)) {
      internalSdkImports.push(`${rel}: internal SDK import`);
    }
    if (/from\s+['"]next\//.test(content)) {
      nextImports.push(`${rel}: stale next/* import`);
    }
  }

  const introStartIssues = scanIntroStartIssues(projectRoot);
  const hardcodedStyleIssues = scanHardcodedStyleIssues(projectRoot);
  const endPageCtaIssues = scanEndPageCtaIssues(projectRoot);
  const matrixStatementIssues = scanMatrixStatementIssues(projectRoot);
  const matrixLayoutIssues = scanMatrixLayoutIssues(projectRoot);
  const mcqOtherIssues = scanMcqOtherIssues(projectRoot);

  return {
    stubMatches,
    internalSdkImports,
    nextImports,
    introStartIssues,
    hardcodedStyleIssues,
    endPageCtaIssues,
    matrixStatementIssues,
    matrixLayoutIssues,
    mcqOtherIssues,
  };
}
